import * as React from 'react';
import Box from '@mui/joy/Box';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Checkbox from '@mui/joy/Checkbox';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Link from '@mui/joy/Link';
import Tooltip from '@mui/joy/Tooltip';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import { visuallyHidden } from '@mui/utils';
import { Stack } from '@mui/joy';
import DeleteButton from './DeleteButton';

/*
interface Data {
    calories: number;
    carbs: number;
    fat: number;
    name: string;
    protein: number;
}
*/

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
    headCells: any[];
    numSelected: number;
    allSelected: boolean;
    onRequestSort: (event: React.MouseEvent<unknown>, property: any/*keyof Data*/) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDelete: () => void;
    order: Order;
    orderBy: string;
    rowCount: number;
    multiSelect: boolean;
  }

export default function EnhancedTableHead(props: EnhancedTableProps) {
    const {
      headCells,
      multiSelect,
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      allSelected,
      rowCount,
      onRequestSort,
      onDelete,
    } = props;

    const createSortHandler =
      (property: any/*keyof Data*/) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      };

    return (
      <thead>
        <tr>
        {multiSelect && (<>
          <th style={{width: '40px'}}>
            <Checkbox
              indeterminate={numSelected > 0 && !allSelected}
              checked={!!allSelected}
              onChange={onSelectAllClick}
              slotProps={{
                input: {
                  'aria-label': 'select all desserts',
                },
              }}
              sx={{ verticalAlign: 'sub' }}
            />
          </th>
          <th colSpan={headCells.length}>
            <Stack
            direction={'row'}
            justifyContent={'space-between'}
            >
              <Box>
                {numSelected} selected
              </Box>
              <Box>
                {/* // other actions could go here */}
              </Box>
            </Stack>
            {/* // delete button here */}
            </th>
          </>)}
          {!multiSelect && headCells.map((headCell, index) => {
            const active = orderBy === headCell.id;
            return (
              <th
                // colSpan={index === headCells.length - 1 ? 2 : 1}
                style={{
                  width: headCell?.width,
                }}
                key={headCell.id}
                aria-sort={
                  active
                    ? ({ asc: 'ascending', desc: 'descending' } as const)[order]
                    : undefined
                }
              >
                {!headCell.sortable && (<>
                <Typography color="neutral" textColor={'text.primary'} fontWeight={'lg'}>{headCell.label}</Typography>
                </>)}
                {headCell.sortable && (<>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  underline="none"
                  color="neutral"
                  textColor={active ? 'primary.plainColor' : 'text.primary'}
                  component="button"
                  onClick={createSortHandler(headCell.id)}
                  fontWeight="lg"
                  startDecorator={
                    headCell.numeric ? (
                      <ArrowDownwardIcon sx={{ opacity: active ? 1 : 0 }} />
                    ) : null
                  }
                  endDecorator={
                    !headCell.numeric ? (
                      <ArrowDownwardIcon sx={{ opacity: active ? 1 : 0 }} />
                    ) : null
                  }
                  sx={{
                    '& svg': {
                      transition: '0.2s',
                      transform:
                        active && order === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)',
                    },
                    'thead:hover &': { '& svg': { opacity: 1 } },
                  }}
                >
                  {headCell.label}
                  {active ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </Link>
                </>)}
              </th>
            );
          })}
          <th style={{width: '48px'}}>
            {multiSelect && (
              <DeleteButton
                content={<>Are you sure you want to delete these?</>}
                tooltip={'Delete Selected'}
                onDelete={onDelete}
                sx={theme => ({
                  '--Icon-color': 'currentColor',
                    bgcolor: theme.palette.danger[500],
                    ':hover': {
                      bgcolor: theme.palette.danger[600],
                    },
                })}
              />
            )}
          {!multiSelect && (
            <Tooltip title={`Sort by ${order === 'desc' ? 'oldest' : 'recently'} updated`} disableInteractive>
            <Link
              underline="none"
              color="neutral"
              textColor={orderBy === 'updated_at' ? 'primary.plainColor' : 'text.primary'}
              component="button"
              onClick={createSortHandler('updated_at')}
              fontWeight="lg"
              startDecorator={<ArrowDownwardIcon sx={{ opacity: orderBy === 'updated_at' ? 1 : 0 }} />}
              sx={{
                '& svg': {
                  transition: '0.2s',
                  transform:
                    orderBy === 'updated_at' && order === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)',
                },
                'thead:hover &': { '& svg': { opacity: 1 } },
              }}
            >
              {' '}
              {orderBy === 'updated_at' ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </Link>
            </Tooltip>
            )}
          </th>
        </tr>
      </thead>
    );
  }
